import type { CreditType } from "@repo/app-types/entities/creditUsage";
import { SELF_SERVE_CREDIT_PACK_OFFERS } from "@repo/app-types/helpers/billing/creditPacks";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal, ModalSize } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconCreditCard } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { usePurchaseCreditPackMutation } from "../../../api/entities/company.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { formatWholeNumber } from "./usageFormatting.js";

interface PurchaseCreditPackModalProps {
  creditType: CreditType;
  isOpen: boolean;
  onClose: () => void;
}

/** How each credit type is named to the customer when buying a pack. */
const PACK_COPY_BY_CREDIT_TYPE: Record<
  CreditType,
  { title: string; unitNoun: string; addedNoun: string }
> = {
  ai_credits: {
    title: "Buy AI credits",
    unitNoun: "credit",
    addedNoun: "AI credits",
  },
  voice_credits: {
    title: "Buy voice agent minutes",
    unitNoun: "minute",
    addedNoun: "voice agent minutes",
  },
};

const formatUsd = (amount: number): string =>
  amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Self-serve credit packs at list price, invoiced to the company's Stripe
 * customer (net 30). Credits are spendable as soon as the purchase completes,
 * before the invoice is paid.
 */
export const PurchaseCreditPackModal: FC<PurchaseCreditPackModalProps> = (
  props,
) => {
  const { creditType, isOpen, onClose } = props;
  const { unitPriceUsd, quantities } =
    SELF_SERVE_CREDIT_PACK_OFFERS[creditType];
  const copy = PACK_COPY_BY_CREDIT_TYPE[creditType];
  const dispatch = useAppDispatch();
  const [selectedQuantity, setSelectedQuantity] = useState(
    quantities[1] ?? quantities[0],
  );
  // One request id per open modal, so a retried click can never buy twice.
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [purchase, { isLoading }] = usePurchaseCreditPackMutation();

  const handleClose = () => {
    setRequestId(crypto.randomUUID());
    onClose();
  };

  const handleConfirm = async () => {
    if (selectedQuantity === undefined) {
      return;
    }
    try {
      await purchase({
        requestId,
        creditType,
        quantity: selectedQuantity,
      }).unwrap();
      showNotification(dispatch, {
        id: `credit-pack-added-${requestId}`,
        primaryMessage: `${formatWholeNumber(selectedQuantity)} ${copy.addedNoun} added`,
        secondaryMessage: "The invoice has been sent to your billing email.",
        type: NotificationType.SUCCESS,
      });
      handleClose();
    } catch {
      showNotification(dispatch, {
        id: `credit-pack-failed-${requestId}`,
        primaryMessage: "The purchase could not be completed",
        secondaryMessage: "Try again, or contact us if it keeps failing.",
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={handleClose} size={ModalSize.Small}>
      <ModalHeader isCloseVisible>{copy.title}</ModalHeader>
      <p className="mt-2 text-sm text-muted-foreground">
        Available immediately and valid for 12 months. Billed at{" "}
        {formatUsd(unitPriceUsd)} per {copy.unitNoun}; the invoice is due 30
        days after it is sent.
      </p>

      <div className="mt-5 grid gap-2 sm:grid-cols-3">
        {quantities.map((quantity) => (
          <button
            aria-pressed={quantity === selectedQuantity}
            className={twMerge(
              "rounded-lg border px-4 py-3 text-left transition-colors",
              quantity === selectedQuantity
                ? "border-indigo-600 bg-indigo-50"
                : "border-border-light bg-white hover:bg-gray-50",
            )}
            key={quantity}
            onClick={() => setSelectedQuantity(quantity)}
            type="button"
          >
            <p className="text-sm font-semibold text-foreground">
              {formatWholeNumber(quantity)} {copy.unitNoun}s
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatUsd(quantity * unitPriceUsd)}
            </p>
          </button>
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button
          isDisabled={isLoading}
          onClick={handleClose}
          variant={ButtonVariant.Secondary}
        >
          Cancel
        </Button>
        <Button
          icon={<IconCreditCard className="size-4" />}
          isLoading={isLoading}
          onClick={handleConfirm}
          variant={ButtonVariant.Primary}
        >
          Confirm purchase
        </Button>
      </div>
    </Modal>
  );
};
